import { create } from 'zustand';
import { play } from '@/lib/sound';
import type { FsNode } from '@/fs/types';

export interface RecycledItem {
  node: FsNode;
  /** Where it came from, so Restore can put it back. */
  originalPath: string;
  /** Folder it was deleted from, shown in the bin's "Original Location". */
  originalLocation: string;
  deletedAt: number;
}

interface RecycleStore {
  items: RecycledItem[];
  /** Lowercased paths, for the O(1) check `fs.list()` does on every render. */
  deleted: Record<string, true>;

  remove(node: FsNode): void;
  /** Delete without the bin: gone, with nothing offering to put it back. */
  destroy(node: FsNode): void;
  /** Follow a path that has moved, for the node itself and everything under it. */
  rekey(from: string, to: string): void;
  restore(path: string): void;
  restoreAll(): void;
  /** Permanently discard one item. */
  purge(path: string): void;
  /** Discard the lot. Chimes — this is XP's "Empty Recycle Bin" sound event. */
  empty(): void;
  isDeleted(path: string): boolean;
}

/**
 * The Recycle Bin.
 *
 * Deliberately NOT persisted. Deleting things is a toy — it should not be
 * possible for a visitor to empty the desktop and have it still be empty when
 * they come back. A reload puts everything back; the bin only has to survive
 * the current session.
 *
 * Within a session it is real, though, and that is the part that matters:
 * `deleted` is a mask `resolve()` consults, so a deleted path stops resolving
 * for everything at once — Explorer, `DIR`, the address bar, and the players,
 * which build their playlists by listing the disk.
 */
export const useRecycleStore = create<RecycleStore>((set, get) => ({
  items: [],
  deleted: {},

  remove(node) {
    const key = node.path.toLowerCase();
    if (get().deleted[key]) return;
    const cut = node.path.lastIndexOf('\\');
    set((s) => ({
      items: [
        ...s.items,
        {
          node,
          originalPath: node.path,
          originalLocation: cut === -1 ? node.path : node.path.slice(0, cut),
          deletedAt: Date.now(),
        },
      ],
      deleted: { ...s.deleted, [key]: true },
    }));
  },

  destroy(node) {
    // The same mask, without an entry beside it. That is the whole difference
    // between the two ways of deleting something, and it is why "Do not move
    // files to the Recycle Bin" needs nothing else to work.
    set((s) => ({ deleted: { ...s.deleted, [node.path.toLowerCase()]: true } }));
  },

  /**
   * A folder that is dragged somewhere else takes its deleted children with
   * it — and everything here is keyed by path, so the keys have to travel too.
   * Without this a file would climb back out of the bin the moment the folder
   * it was deleted from was moved.
   */
  rekey(from, to) {
    const oldKey = from.toLowerCase();
    const prefix = `${oldKey}\\`;

    /** The path this one becomes, or null if the move does not touch it. */
    const moved = (path: string): string | null => {
      const key = path.toLowerCase();
      if (key === oldKey) return to;
      if (key.startsWith(prefix)) return to + path.slice(from.length);
      return null;
    };

    set((s) => {
      let touched = false;

      const deleted: Record<string, true> = {};
      for (const key of Object.keys(s.deleted)) {
        const next = moved(key);
        if (next === null) deleted[key] = true;
        else {
          deleted[next.toLowerCase()] = true;
          touched = true;
        }
      }

      const items = s.items.map((item) => {
        const next = moved(item.originalPath);
        if (next === null) return item;
        touched = true;
        const cut = next.lastIndexOf('\\');
        return {
          ...item,
          originalPath: next,
          originalLocation: cut === -1 ? next : next.slice(0, cut),
        };
      });

      return touched ? { deleted, items } : s;
    });
  },

  restore(path) {
    const key = path.toLowerCase();
    set((s) => {
      const { [key]: _removed, ...rest } = s.deleted;
      return { items: s.items.filter((i) => i.originalPath.toLowerCase() !== key), deleted: rest };
    });
  },

  restoreAll() {
    set({ items: [], deleted: {} });
  },

  purge(path) {
    const key = path.toLowerCase();
    // Stays masked out: purged means gone, not restored.
    set((s) => ({ items: s.items.filter((i) => i.originalPath.toLowerCase() !== key) }));
  },

  empty() {
    // The chime belongs here rather than at the call sites: emptying the bin is
    // a shell event, and it is reachable from the bin's own toolbar, its File
    // menu and the desktop icon's context menu. Wiring it up per menu is how
    // one of them ends up silent.
    if (get().items.length === 0) return;
    play('recycle');
    set((s) => ({ items: [], deleted: s.deleted }));
  },

  isDeleted(path) {
    return get().deleted[path.toLowerCase()] === true;
  },
}));

/** Non-reactive read, for `fs.list()` which is called outside React too. */
export function isDeletedPath(path: string): boolean {
  return useRecycleStore.getState().deleted[path.toLowerCase()] === true;
}

/** The app the desktop's Recycle Bin node launches. */
export const RECYCLE_BIN_APP_ID = 'recyclebin';

/**
 * True for the bin itself, wherever it turns up. The desktop uses it to swap
 * in the full-bin icon, and the shell to give it its own context menu — you
 * cannot put the Recycle Bin in the Recycle Bin.
 */
export function isRecycleBinNode(node: FsNode): boolean {
  return node.type === 'file' && node.data.kind === 'app' && node.data.appId === RECYCLE_BIN_APP_ID;
}

/** Icon for the bin, which XP drew differently when there was something in it. */
export function recycleBinIcon(full: boolean): string {
  return full ? '/assets/icons/recycle-bin-full.svg' : '/assets/icons/recycle-bin.svg';
}
